import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { useTranslation } from 'react-i18next';
import {
  Form, message, Typography,
} from 'antd';
import { useHistory } from 'react-router';
import { createOrder, fetchAvailableCompanies } from '../../../store/actions';
import {
  getMeId, getOrderCreationModalData, getSelectedCompany,
} from '../../../store/selectors';
import { isSuccess } from '../../../utils/responseUtils';
import { error } from '../../../services/Logging';
import { OrderMapper } from '../../../types/mappers';
import OrderCreationForm from './Components/OrderCreationForm';
import OrderConfirmationDialog from './Components/OrderConfirmationDialog';
import { OrderType } from '../../../types';

const OrderCreationPage = () => {
  const { companies, creation } = useSelector(getOrderCreationModalData);
  const contextCompanyId = useSelector(getSelectedCompany);
  const loggedUserId = useSelector(getMeId);

  const [confirmationDialog, setConfirmationDialog] = useState(null);
  const [submitInProgress, setSubmitInProgress] = useState(false);

  const { t } = useTranslation();
  const [commonForm] = Form.useForm();
  const [timeMonitoringForm] = Form.useForm();
  const [tourMonitoringForm] = Form.useForm();
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchAvailableCompanies());
  }, [dispatch]);

  const SuccessMessageContent = ({ internalId }) => (
    <>
      <Typography.Text>
        {t('ORDER.CREATION_SUCCESSFUL', { internalId })}
      </Typography.Text>
    </>
  );

  SuccessMessageContent.propTypes = {
    internalId: PropTypes.string.isRequired,
  };

  const orderHasStops = (order) => order.stops && order.stops.length;

  const getOrderType = (order) => (orderHasStops(order) ? OrderType.TOUR : OrderType.TIME);

  const defineConfirmationTitle = (orderType) => (orderType === OrderType.TIME
    ? t('ORDER.CONFIRMATION.TIME_TITLE')
    : t('ORDER.CONFIRMATION.TOUR_TITLE'));

  const dispatchCreateOrder = (order, orderType) => {
    if (order) {
      setConfirmationDialog({
        order,
        orderType,
        title: defineConfirmationTitle(orderType),
        onSubmit: () => {
          setSubmitInProgress(true);
          dispatch(createOrder(order, contextCompanyId))
            .then((response) => {
              setConfirmationDialog(null);
              if (isSuccess(response)) {
                message.success(<SuccessMessageContent internalId={response.payload.data.internalId} />, 7);
                history.push({ pathname: '/orders/outgoing/'.concat(getOrderType(response.payload.data)) });
              }
              setSubmitInProgress(false);
            });
        },
      });
    }
  };

  const getFormToValidate = (orderType) => (orderType === OrderType.TIME
    ? timeMonitoringForm
    : tourMonitoringForm
  );

  const handleSubmit = (orderType) => {
    Promise.all([commonForm.validateFields(), getFormToValidate(orderType).validateFields()])
      .then((order) => OrderMapper.formToCreateRequest(order, { loggedUserId, contextCompanyId }, orderType), error)
      .then((order) => dispatchCreateOrder(order, orderType), error);
  };

  const handleCancel = () => {
    commonForm.resetFields();
    timeMonitoringForm.resetFields();
    tourMonitoringForm.resetFields();
    history.goBack();
  };

  return (
    <>
      <BreadcrumbsItem to="/orders/new">{t('BREADCRUMB.MONITORING_NEW')}</BreadcrumbsItem>
      <OrderCreationForm
        commonForm={commonForm}
        timeMonitoringForm={timeMonitoringForm}
        tourMonitoringForm={tourMonitoringForm}
        companies={companies}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        creation={creation}
      />
      <OrderConfirmationDialog
        open={!!confirmationDialog}
        title={confirmationDialog && confirmationDialog.title}
        onClose={() => setConfirmationDialog(null)}
        onSubmit={confirmationDialog && confirmationDialog.onSubmit}
        order={confirmationDialog && confirmationDialog.order}
        companies={companies && companies.data}
        orderType={confirmationDialog && confirmationDialog.orderType}
        disabled={submitInProgress}
      />
    </>
  );
};

export default OrderCreationPage;
